/**
 * Kite WebSocket Service
 * Manages real-time data streaming from Kite Ticker
 */

export interface TickData {
  token: number;
  lastPrice: number;
  lastQuantity?: number;
  averagePrice?: number;
  volume?: number;
  buyQuantity?: number;
  sellQuantity?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  change?: number;
  changePercent?: number;
  lastTradeTime?: Date;
  oi?: number;
  oiDayHigh?: number;
  oiDayLow?: number;
  bid?: { price: number; quantity: number }[];
  ask?: { price: number; quantity: number }[];
}

export interface InstrumentToken {
  token: number;
  symbol: string;
  type: 'INDEX' | 'CE' | 'PE';
  strike?: number;
  expiry?: string;
  exchange: string;
}

type TickCallback = (ticks: Map<number, TickData>) => void;
type StatusCallback = (status: 'connecting' | 'connected' | 'disconnected' | 'error', message?: string) => void;

class KiteWebSocketService {
  private ws: WebSocket | null = null;
  private apiKey: string = '';
  private accessToken: string = '';
  private subscribedTokens: number[] = [];
  private tokenMap: Map<number, InstrumentToken> = new Map();
  private tickCallbacks: TickCallback[] = [];
  private statusCallbacks: StatusCallback[] = [];
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 3000;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isManualDisconnect: boolean = false;

  /**
   * Initialize WebSocket with credentials
   */
  initialize(apiKey: string, accessToken: string): void {
    this.apiKey = apiKey;
    this.accessToken = accessToken;
  }

  /**
   * Connect to Kite WebSocket
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.apiKey || !this.accessToken) {
        reject(new Error('API key and access token required'));
        return;
      }

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      this.isManualDisconnect = false;
      this.notifyStatus('connecting');

      const wsUrl = `wss://ws.kite.trade?api_key=${this.apiKey}&access_token=${this.accessToken}`;

      try {
        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
          console.log('Kite WebSocket connected');
          this.reconnectAttempts = 0;
          this.notifyStatus('connected');
          this.startHeartbeat();

          // Resubscribe to previously subscribed tokens
          if (this.subscribedTokens.length > 0) {
            this.subscribe(this.subscribedTokens);
          }

          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          console.error('Kite WebSocket error:', error);
          this.notifyStatus('error', 'WebSocket connection error');
        };

        this.ws.onclose = (event) => {
          console.log('Kite WebSocket closed:', event.code, event.reason);
          this.stopHeartbeat();

          if (!this.isManualDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.notifyStatus('disconnected', 'Reconnecting...');
            this.reconnectAttempts++;
            setTimeout(() => this.connect(), this.reconnectDelay);
          } else {
            this.notifyStatus('disconnected', 'Connection closed');
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.isManualDisconnect = true;
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.subscribedTokens = [];
    this.notifyStatus('disconnected', 'Manually disconnected');
  }

  /**
   * Subscribe to instrument tokens
   */
  subscribe(tokens: number[], mode: 'ltp' | 'quote' | 'full' = 'quote'): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot subscribe');
      return;
    }

    // Add to subscribed list
    tokens.forEach(token => {
      if (!this.subscribedTokens.includes(token)) {
        this.subscribedTokens.push(token);
      }
    });

    // Send subscribe message
    const message = JSON.stringify({
      a: 'subscribe',
      v: tokens
    });
    this.ws.send(message);

    // Set mode
    const modeMessage = JSON.stringify({
      a: 'mode',
      v: [mode, tokens]
    });
    this.ws.send(modeMessage);

    console.log(`Subscribed to ${tokens.length} tokens in ${mode} mode`);
  }

  /**
   * Unsubscribe from instrument tokens
   */
  unsubscribe(tokens: number[]): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Remove from subscribed list
    this.subscribedTokens = this.subscribedTokens.filter(t => !tokens.includes(t));

    const message = JSON.stringify({
      a: 'unsubscribe',
      v: tokens
    });
    this.ws.send(message);
  }

  /**
   * Set token mapping for symbol lookup
   */
  setTokenMap(instruments: InstrumentToken[]): void {
    this.tokenMap.clear();
    instruments.forEach(inst => {
      this.tokenMap.set(inst.token, inst);
    });
  }

  /**
   * Get instrument info by token
   */
  getInstrument(token: number): InstrumentToken | undefined {
    return this.tokenMap.get(token);
  }

  /**
   * Register tick callback
   */
  onTick(callback: TickCallback): () => void {
    this.tickCallbacks.push(callback);
    return () => {
      this.tickCallbacks = this.tickCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Register status callback
   */
  onStatus(callback: StatusCallback): () => void {
    this.statusCallbacks.push(callback);
    return () => {
      this.statusCallbacks = this.statusCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: ArrayBuffer | string): void {
    if (typeof data === 'string') {
      // Text message (usually connection info or errors)
      try {
        const json = JSON.parse(data);
        console.log('Kite WebSocket message:', json);
      } catch {
        console.log('Kite WebSocket text:', data);
      }
      return;
    }

    // Binary message (tick data)
    const ticks = this.parseBinaryMessage(data);
    if (ticks.size > 0) {
      this.tickCallbacks.forEach(cb => cb(ticks));
    }
  }

  /**
   * Parse binary tick data from Kite
   */
  private parseBinaryMessage(buffer: ArrayBuffer): Map<number, TickData> {
    const ticks = new Map<number, TickData>();
    const view = new DataView(buffer);

    if (buffer.byteLength < 2) return ticks;

    const numberOfPackets = view.getInt16(0);
    let offset = 2;

    for (let i = 0; i < numberOfPackets; i++) {
      if (offset >= buffer.byteLength) break;

      const packetLength = view.getInt16(offset);
      offset += 2;

      if (offset + packetLength > buffer.byteLength) break;

      const token = view.getInt32(offset);

      const tick: TickData = {
        token,
        lastPrice: 0
      };

      if (packetLength === 8) {
        // LTP mode
        tick.lastPrice = view.getInt32(offset + 4) / 100;
      } else if (packetLength === 28) {
        // Quote mode (indices)
        tick.lastPrice = view.getInt32(offset + 4) / 100;
        tick.high = view.getInt32(offset + 8) / 100;
        tick.low = view.getInt32(offset + 12) / 100;
        tick.open = view.getInt32(offset + 16) / 100;
        tick.close = view.getInt32(offset + 20) / 100;
        tick.change = view.getInt32(offset + 24) / 100;
      } else if (packetLength === 32) {
        // Quote mode (stocks)
        tick.lastPrice = view.getInt32(offset + 4) / 100;
        tick.lastQuantity = view.getInt32(offset + 8);
        tick.averagePrice = view.getInt32(offset + 12) / 100;
        tick.volume = view.getInt32(offset + 16);
        tick.buyQuantity = view.getInt32(offset + 20);
        tick.sellQuantity = view.getInt32(offset + 24);
        tick.open = view.getInt32(offset + 28) / 100;
      } else if (packetLength >= 44) {
        // Full mode
        tick.lastPrice = view.getInt32(offset + 4) / 100;
        tick.lastQuantity = view.getInt32(offset + 8);
        tick.averagePrice = view.getInt32(offset + 12) / 100;
        tick.volume = view.getInt32(offset + 16);
        tick.buyQuantity = view.getInt32(offset + 20);
        tick.sellQuantity = view.getInt32(offset + 24);
        tick.open = view.getInt32(offset + 28) / 100;
        tick.high = view.getInt32(offset + 32) / 100;
        tick.low = view.getInt32(offset + 36) / 100;
        tick.close = view.getInt32(offset + 40) / 100;

        if (packetLength >= 184) {
          // With depth data
          tick.oi = view.getInt32(offset + 172);
          tick.oiDayHigh = view.getInt32(offset + 176);
          tick.oiDayLow = view.getInt32(offset + 180);
        }
      }

      // Calculate change percent
      if (tick.close && tick.close > 0) {
        tick.change = tick.lastPrice - tick.close;
        tick.changePercent = ((tick.lastPrice - tick.close) / tick.close) * 100;
      }

      ticks.set(token, tick);
      offset += packetLength;
    }

    return ticks;
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Kite uses binary ping/pong, browser handles this automatically
        // We just check connection is alive
      }
    }, 30000);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Notify status callbacks
   */
  private notifyStatus(status: 'connecting' | 'connected' | 'disconnected' | 'error', message?: string): void {
    this.statusCallbacks.forEach(cb => cb(status, message));
  }
}

// Export singleton instance
export const kiteWebSocket = new KiteWebSocketService();
export default kiteWebSocket;
